import React from 'react';
import {
    Timeline,
    TimelineContent,
    TimelineDot,
} from '@mui/lab';
import { styled, Typography, Box, tooltipClasses } from '@mui/material';
import Lottie from 'react-lottie';
import RedBlinkingLottie from '../../../Components/Lotties/RedBlinkingLottie.json';
import OrangeBlinkingLottie from '../../../Components/Lotties/OrangeBlinkingLottie.json';
import YellowBlinkingLottie from '../../../Components/Lotties/YellowBlinkingLottie.json';
import { PrimaryTooltip } from '../../../Components/StyledComponent';
import { RESOLVED_STATUSES } from '../AlertConfigs';

const severityStyles = {
    critical: '#B50500',
    'high severity': '#FF6B0F',
    warning: '#DCA101',
    default: '#DCA101',
}

const StyledTimeline = styled(Timeline)`
    padding: 8px 0;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    margin: 0;
    padding: 0;
    position: relative;
    & .MuiTimelineItem-root {
        min-height: auto;
        padding: 0;
        margin: 0;
        flex: 1;
    }
    & .MuiTimelineItem-root:before {
        display: none;
    }
`;

const StyledTimelineContent = styled(TimelineContent)`
    padding: 0px;
    margin: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 36px;
`;

const StyledTimelineDot = styled(TimelineDot)(({ variant = 'circle', position = 100, severityColor = '#B50500' }) => ({
    margin: 0,
    padding: 0,
    width: '10px', /* actual width 14px: 12px + 2px border each side */
    height: '10px', /* actual height 14px: 12px + 2px border each side */
    backgroundColor: severityColor,
    border: `2px solid ${severityColor}`,
    borderRadius: variant === 'square' ? '2px' : '50%',
    position: 'absolute',
    top: '10px',
    left: position === 100 ? `calc(${position}% - 14px)` : `${position}%`, /* 14px is the width of the timeline dot */
    boxShadow: 'none',
}))

const StyledTimelineAnimate = styled(StyledTimelineDot)`
    background-color: transparent;
    border: none;
    width: auto;
    height: auto;
    top: 4px;
`;

const DateText = styled(Typography)`
    color: ${props => props.theme.palette.text.secondary};
    font-size: 0.875rem;
    margin-top: 8px;
`;

const SeparatorText = styled(Typography)`
    color: ${props => props.theme.palette.text.primary};
    font-size: 1rem;
    margin-top: 8px;
`;

const StartEndDate = styled(Typography)`
    color: #494949;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
`;

const TimelineWrapper = styled(Box)`
    width: 100%;
    position: relative;
    margin-top: 4px;
    margin-bottom: 4px;
    padding-right: 20px;
`;

const SeparatorContent = styled(StyledTimelineContent)`
    position: absolute;
    left: ${props => props.position}%;
    width: 100%;
`;

const TimelineItemWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    width: 100%;

    &:before {
        content: '';
        position: absolute;
        top: 16px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: ${props => props.color ? props.color : '#B50500'};
        z-index: 0;
    }
`;

function calculatePosition(date, startDate, endDate) {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const current = new Date(date).getTime();

    const totalDuration = end - start;
    const currentPosition = current - start;

    return (currentPosition / totalDuration) * 100;
}

const AnimatedLottie = ({ severity = '' }) => {
    const severityLottieMap = {
        critical: RedBlinkingLottie,
        'high severity': OrangeBlinkingLottie,
        warning: YellowBlinkingLottie,
        default: RedBlinkingLottie,
    }
    const animationData = severityLottieMap[severity?.toLowerCase()] ?? severityLottieMap['default'];
    return (
        <Lottie
            options={{
                loop: true,
                autoplay: true,
                animationData,
                rendererSettings: {
                    preserveAspectRatio: "xMidYMid slice"
                }
            }}
            height={26}
            width={26}
        />
    )
}

const HorizontalTimeline = ({ data }) => {
    const { start, timelineSeparators, end } = data;

    const getSeverityColor = (severity) => {
        const severityKey = severity?.toLowerCase();
        if (severityKey && severityStyles?.[severityKey]) {
            return severityStyles[severityKey];
        }
        return severityStyles['default'];
    }

    return (
        <TimelineWrapper>
            <StyledTimeline>
                {/* Start Date */}
                <TimelineItemWrapper
                    color={getSeverityColor(start?.severity)}
                >
                    <StyledTimelineDot
                        variant="square"
                        position={0}
                        severityColor={getSeverityColor(start?.severity)}
                    />
                    <StyledTimelineContent>
                        <Typography variant="fw400s14c494949" sx={{ lineHeight: '20px' }}>{start.label}</Typography>
                        <StartEndDate>
                            {start.subLabel}
                        </StartEndDate>
                        {start?.severityLabel && (
                            <StartEndDate>
                                {start.severityLabel}
                            </StartEndDate>
                        )}
                    </StyledTimelineContent>
                </TimelineItemWrapper>

                {/* Timeline Separators */}
                {timelineSeparators.map((separator, index) => {
                    const position = calculatePosition(separator.date, start.date, end.date);
                    return (
                        <TimelineItemWrapper
                            key={index}
                            color={getSeverityColor(separator?.severity)}
                        >
                            <PrimaryTooltip
                                sx={{
                                    [`& .${tooltipClasses.tooltip}`]: {
                                        maxWidth: "150px",
                                        textAlign: "left !important",
                                    }
                                }}
                                title={
                                    <>
                                        <Typography variant="fw400s12" component="div">
                                            <span style={{ fontWeight: 600 }}>{separator.changeType}:</span>
                                            <br />
                                            <span>{separator.subLabel}</span>
                                            <br />
                                            <span>{separator.severityLabel}</span>
                                        </Typography>
                                    </>
                                }
                                position="bottom"
                            >
                                <StyledTimelineDot
                                    position={position}
                                    severityColor={getSeverityColor(separator?.severity)}
                                />
                            </PrimaryTooltip>
                        </TimelineItemWrapper>
                    );
                })}

                {/* End Date */}
                <TimelineItemWrapper
                    sx={{ alignItems: 'flex-end' }}
                    color={getSeverityColor(end?.severity)}
                >
                    {RESOLVED_STATUSES.includes(end.label) ? (
                        <StyledTimelineDot
                            severityColor={"#09B820"}
                            variant="square"
                            position={100}
                        />
                    ) : (
                        <StyledTimelineAnimate position={100}>
                            <AnimatedLottie severity={end.severity} />
                        </StyledTimelineAnimate>
                    )}
                    <SeparatorContent sx={{ alignItems: 'flex-end' }}>
                        <Typography variant="fw400s14c494949" sx={{ lineHeight: '20px' }}>{end.label}</Typography>
                        <StartEndDate>
                            {end.subLabel}
                        </StartEndDate>
                        {end?.severityLabel && (
                            <StartEndDate>
                                {end.severityLabel}
                            </StartEndDate>
                        )}
                    </SeparatorContent>
                </TimelineItemWrapper>
            </StyledTimeline>
        </TimelineWrapper>
    );
}

export default HorizontalTimeline;
